import { types, getEnv, flow } from 'mobx-state-tree';
import { reaction } from 'mobx';
import { ViewPage, PageSchema } from '../types/pageTypes';
import { DirectusPageService, PageOperationResult } from '../services/pageService';

// 页面信息模型（对应 view_pages 表结构）
const ViewPageModel = types
  .model('ViewPage', {
    route: types.identifier,    // 主键
    name: types.string,         // 页面名称
    icon: types.optional(types.string, 'fa fa-file'), // 图标
    json_file: types.maybeNull(types.string)  // JSON文件UUID
  });

export const MainStore = types
  .model('MainStore', {
    pages: types.optional(types.array(ViewPageModel), []),
    theme: 'cxd',
    asideFixed: true,
    asideFolded: false,
    offScreen: false,
    addPageIsOpen: false,
    preview: false,
    isMobile: false,
    schema: types.frozen(),
    currentPageRoute: types.optional(types.string, ''),
    // 操作状态管理
    isLoading: types.optional(types.boolean, false),
    operationMessage: types.optional(types.string, ''),
    hasUnsavedChanges: types.optional(types.boolean, false),
    lastSavedSchema: types.frozen()
  })
  .views(self => ({
    get fetcher() {
      return getEnv(self).fetcher;
    },
    get notify() {
      return getEnv(self).notify;
    },
    get alert() {
      return getEnv(self).alert;
    },
    get copy() {
      return getEnv(self).copy;
    },
    get directusApi() {
      return getEnv(self).directusApi;
    },
    get AGIMX_APP_MODE() {
      return getEnv(self).AGIMX_APP_MODE;
    },
    get currentPage(): ViewPage | null {
      return self.pages.find(page => page.route === self.currentPageRoute) || null;
    },
    get currentPageIndex(): number {
      return self.pages.findIndex(page => page.route === self.currentPageRoute);
    },
    get needsSave(): boolean {
      // 如果没有当前页面或正在加载，不需要保存
      if (!self.currentPageRoute || self.isLoading) {
        return false;
      }
      
      // 如果没有 schema 或 lastSavedSchema，不需要保存
      if (!self.schema || !self.lastSavedSchema) {
        return false;
      }
      return JSON.stringify(self.lastSavedSchema) !== JSON.stringify(self.schema);
    }
  }))
  .actions(self => {
    let pageService: DirectusPageService | null = null;

    function initializePageService() {
      if (!pageService && self.directusApi) {
        const config = {
          collectionName: 'view_pages',
        };
        pageService = new DirectusPageService(self.directusApi, config);
      }
    }

    // UI状态管理方法
    function toggleAsideFolded() {
      self.asideFolded = !self.asideFolded;
    }

    function toggleAsideFixed() {
      self.asideFixed = !self.asideFixed;
    }

    function toggleOffScreen() {
      self.offScreen = !self.offScreen;
    }

    function setAddPageIsOpen(isOpened: boolean) {
      self.addPageIsOpen = isOpened;
    }

    function setPreview(value: boolean) {
      self.preview = value;
    }

    function setIsMobile(value: boolean) {
      self.isMobile = value;
    }

    function updateSchema(value: any) {
      self.schema = value;
      if (self.lastSavedSchema && JSON.stringify(self.lastSavedSchema) !== JSON.stringify(value)) {
        self.hasUnsavedChanges = true;
      }
    }

    function setLastSavedSchema(schema: any) {
      self.lastSavedSchema = schema;
      self.hasUnsavedChanges = false;
    }

    function clearUnsavedChanges() {
      self.hasUnsavedChanges = false;
    }

    // 🔧 新增：清空 schema 的方法
    function clearSchema() {
      self.schema = undefined;
      self.lastSavedSchema = undefined;
      self.hasUnsavedChanges = false;
      self.currentPageRoute = '';
      self.operationMessage = '';
    }

    function setCurrentPage(route: string) {
      self.currentPageRoute = route;
    }

    function setCurrentPageByIndex(index: number) {
      if (index >= 0 && index < self.pages.length) {
        self.currentPageRoute = self.pages[index].route;
      }
    }

    function setOperationMessage(message: string) {
      self.operationMessage = message;
    }

    // === 页面管理方法 ===

    /**
     * 加载所有页面列表
     */
    const loadPages = flow(function* () {
      initializePageService();
      if (!pageService) {
        throw new Error('页面服务未初始化');
      }

      self.isLoading = true;
      self.operationMessage = '正在加载页面列表...';

      try {
        const result: PageOperationResult = yield pageService.getAllPages();
        
        if (result.success) {
          self.pages.clear();
          result.data.forEach((pageData: ViewPage) => {
            self.pages.push(ViewPageModel.create(pageData));
          });
          self.operationMessage = '页面列表加载成功';
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '加载页面列表失败';
        self.operationMessage = errorMessage;
        console.error('加载页面列表失败:', error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    });

    /**
     * 加载页面配置
     */
    const loadPageSchema = flow(function* (route: string) {
      initializePageService();
      if (!pageService) {
        throw new Error('页面服务未初始化');
      }

      self.isLoading = true;
      self.operationMessage = '正在加载页面配置...';

      try {
        const result: PageOperationResult = yield pageService.getPageSchema(route);
        
        if (result.success) {
          // 🔧 关键修复：同时设置，避免中间状态
          const schemaData = result.data;
          self.schema = result.data;
          self.lastSavedSchema = schemaData; // 同时设置，保持一致
          // setLastSavedSchema(result.data);
          self.operationMessage = '页面配置加载成功';
          return result.data;
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '加载页面配置失败';
        self.operationMessage = errorMessage;
        console.error('加载页面配置失败:', error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    });

    /**
     * 创建新页面
     */
    const createPage = flow(function* (pageData: {
      route: string;
      name: string;
      icon?: string;
      schema?: PageSchema;
    }) {
      initializePageService();
      if (!pageService) {
        throw new Error('页面服务未初始化');
      }

      self.isLoading = true;
      self.operationMessage = '正在创建页面...';

      try {
        const result: PageOperationResult = yield pageService.createPage(pageData);
        
        if (result.success) {
          // 添加到本地store
          self.pages.push(ViewPageModel.create(result.data));
          self.operationMessage = '页面创建成功';
          return result.data;
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '创建页面失败';
        self.operationMessage = errorMessage;
        console.error('创建页面失败:', error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    });

    /**
     * 更新页面配置
     */
    const updatePageSchema = flow(function* (route: string, schema: PageSchema) {
      initializePageService();
      if (!pageService) {
        throw new Error('页面服务未初始化');
      }
      self.isLoading = true;
      self.operationMessage = '正在保存页面配置...';

      try {
        const result: PageOperationResult = yield pageService.updatePageSchema(route, schema);
        
        if (result.success) {
          setLastSavedSchema(schema);
          self.operationMessage = '页面配置保存成功';
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '保存页面配置失败';
        self.operationMessage = errorMessage;
        console.error('保存页面配置失败:', error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    });

    /**
     * 更新当前页面配置
     */
    const saveCurrentPage = flow(function* () {
      if (!self.currentPageRoute || !self.schema) {
        throw new Error('没有当前页面或配置');
      }

      yield updatePageSchema(self.currentPageRoute, self.schema);
    });

    /**
     * 删除页面
     */
    const deletePage = flow(function* (route: string) {
      initializePageService();
      if (!pageService) {
        throw new Error('页面服务未初始化');
      }

      self.isLoading = true;
      self.operationMessage = '正在删除页面...';

      try {
        const result: PageOperationResult = yield pageService.deletePage(route);
        
        if (result.success) {
          // 从本地store移除
          const index = self.pages.findIndex(page => page.route === route);
          if (index >= 0) {
            self.pages.splice(index, 1);
          }

          // 如果删除的是当前页面，清空当前页面
          if (self.currentPageRoute === route) {
            self.currentPageRoute = '';
            self.schema = undefined;
            self.lastSavedSchema = undefined;
            self.hasUnsavedChanges = false;
          }

          self.operationMessage = '页面删除成功';
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '删除页面失败';
        self.operationMessage = errorMessage;
        console.error('删除页面失败:', error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    });

    /**
     * 根据索引删除页面
     */
    const removePageAt = flow(function* (index: number) {
      if (index < 0 || index >= self.pages.length) {
        throw new Error('页面索引无效');
      }

      const page = self.pages[index];
      yield deletePage(page.route);
    });

    /**
     * 获取pageService实例（供外部使用）
     */
    function getPageService(): DirectusPageService | null {
      initializePageService();
      return pageService;
    }

    return {
      toggleAsideFolded,
      toggleAsideFixed,
      toggleOffScreen,
      setAddPageIsOpen,
      setPreview,
      setIsMobile,
      updateSchema,
      setCurrentPage,
      setCurrentPageByIndex,
      setLastSavedSchema,
      clearUnsavedChanges,
      clearSchema,
      setOperationMessage,
      // 页面管理方法
      loadPages,
      loadPageSchema,
      createPage,
      updatePageSchema,
      saveCurrentPage,
      deletePage,
      removePageAt,
      getPageService,
      
      afterCreate() {
        // 延迟初始化，确保directusApi已经注入
        setTimeout(() => {
          initializePageService();
          
          // 加载页面列表
          loadPages().catch(error => {
            console.error('初始化加载页面失败:', error);
          });
        }, 0);
        
        // 持久化UI状态
        if (typeof window !== 'undefined' && window.localStorage) {
          const storeData = window.localStorage.getItem('store');
          if (storeData) {
            try {
              const parsed = JSON.parse(storeData);
              if (parsed.theme) self.theme = parsed.theme;
              if (typeof parsed.asideFixed === 'boolean') self.asideFixed = parsed.asideFixed;
              if (typeof parsed.asideFolded === 'boolean') self.asideFolded = parsed.asideFolded;
              if (typeof parsed.preview === 'boolean') self.preview = parsed.preview;
              if (typeof parsed.isMobile === 'boolean') self.isMobile = parsed.isMobile;
            } catch (error) {
              console.warn('恢复localStorage数据失败:', error);
            }
          }

          reaction(
            () => ({
              theme: self.theme,
              asideFixed: self.asideFixed,
              asideFolded: self.asideFolded,
              preview: self.preview,
              isMobile: self.isMobile
            }),
            json => {
              window.localStorage.setItem('store', JSON.stringify(json));
            }
          );
        }
      }
    };
  });

export type IMainStore = typeof MainStore.Type;